<script setup lang="ts">
/**
 * design-system.md -> Components -> Button. 40px tall (size="md") or 36px (size="sm" — the table
 * "Revoke" button uses this), radius 8, Geist 14/500, icon 16-18px with an 8px gap. One primary
 * per view. Disabled is always a recolour (`line` fill, `ink-3` text), never opacity.
 *
 * Renders a real <a> when `href` is given (navigation only — never for an action), a real <button>
 * otherwise. Icon + label go in the default slot; Button only supplies the flex/gap wrapper.
 */
import { computed } from 'vue';

type Variant = 'primary' | 'secondary' | 'text' | 'destructive' | 'icon';

const props = withDefaults(
  defineProps<{
    variant?: Variant;
    size?: 'md' | 'sm';
    type?: 'button' | 'submit';
    disabled?: boolean;
    href?: string;
    ariaLabel?: string;
  }>(),
  { variant: 'secondary', size: 'md', type: 'button', disabled: false }
);

const isIcon = computed(() => props.variant === 'icon');

const variantClasses: Record<string, string> = {
  primary: 'border border-transparent bg-ink text-on-ink hover:bg-link-hover',
  secondary: 'border border-line-strong bg-surface text-ink hover:bg-wash',
  text: 'border-0 bg-transparent text-ink underline underline-offset-3 decoration-line-strong hover:decoration-ink',
  destructive: 'border border-line bg-surface text-danger-fg hover:bg-danger-bg/40',
  icon: 'border border-line bg-surface text-ink hover:bg-wash'
};

const sizeClasses = computed(() => {
  if (isIcon.value) {
    return props.size === 'sm' ? 'size-9' : 'size-10';
  }
  if (props.variant === 'text') {
    return props.size === 'sm' ? 'h-9 px-1' : 'h-10 px-1';
  }
  return props.size === 'sm' ? 'h-9 px-3 gap-2 type-small font-medium' : 'h-10 pr-4 pl-3 gap-2 type-control';
});
</script>

<template>
  <component
    :is="href ? 'a' : 'button'"
    :href="href"
    :type="href ? undefined : type"
    :disabled="!href && disabled"
    :aria-disabled="href && disabled ? 'true' : undefined"
    :aria-label="ariaLabel"
    class="type-control inline-flex shrink-0 items-center justify-center rounded-control whitespace-nowrap transition-colors disabled:pointer-events-none disabled:border-transparent disabled:bg-line disabled:text-ink-3 disabled:hover:bg-line"
    :class="[sizeClasses, variantClasses[variant]]"
  >
    <slot />
  </component>
</template>
